import {useColorModeValue, useMediaQuery} from '@chakra-ui/react';
import React, {memo, useEffect, useRef, useState} from 'react';
import {GiHamburgerMenu} from 'react-icons/gi';
import {useDispatch} from 'react-redux';
import {Link, useHistory, useLocation} from 'react-router-dom';
import {
  BasicRoute,
  Row,
  UiBox,
  UiButton,
  UiContainer,
  UiGrid,
  UiGridItem,
  UiIcon,
  UiImage,
  UiLink,
  UiText,
} from '~/components/elements';
import {MobileNavigation} from '~/components/layouts/MobileNavigation';
import {PAGES, QUERY_MOBILE} from '~/constants';
import {PAGES_BANNER_SAME_TEXT_COLOR_NAVIGATION} from '~/constants/index';
import {AppDispatch} from '~/redux/root-store';
import {setMenuHeight, showMobileMenu} from '~/redux/ui/slice';
import {publicRoutes} from '~/routes';
import {getCurrentUserFromStorage} from '~/services/auth';
import {isSubstringArrayInString} from '~/utils/helpers';

interface Props {}

export const renderMenuItems = (item: BasicRoute, parentPath = '', isMobile = false, cb: any = undefined): any => {
  if (item.routes) {
    if (item.label) {
      // Mean has nested children
      return renderParentMenuItem(item, item.path, isMobile, cb);
    }
    // Mean this is a layout component
    return item.routes.map(route => {
      return renderMenuItems(route, parentPath, isMobile, cb);
    });
  }
  if (item.label) {
    const fullPath = parentPath && parentPath !== '/' ? `${parentPath}${item.path}` : item.path;
    return <SingleMenuItem key={fullPath} item={item} parentPath={parentPath} isMobile={isMobile} cb={cb} />;
  }
  return null;
};

const renderParentMenuItem = (item: BasicRoute, parentPath = '', isMobile = false, cb: any = undefined) => {
  return (
    <UiBox key={`parent-menu-${item.path}`} _hover={{cursor: 'pointer'}}>
      <Row px={5} alignItems="flex-end">
        <UiLink _hover={{color: 'link.active'}}>{item.label}</UiLink>
      </Row>
    </UiBox>
  );
};

const SingleMenuItem = ({
  item,
  parentPath = '',
  isMobile = false,
  cb = undefined,
}: {
  item: BasicRoute;
  parentPath: string;
  isMobile: boolean;
  cb: any;
}) => {
  const dispatch: AppDispatch = useDispatch();
  const {pathname} = useLocation();
  const isProductsPath = item.path === PAGES.RAYYONE_TOOLS;

  const fullPath = parentPath && parentPath !== '/' ? `${parentPath}${item.path}` : item.path;
  const isActive = pathname.includes(fullPath);

  if (fullPath !== item.path) {
    // Submenu
    return (
      <UiBox key={`menu-${fullPath}`} fontWeight="bold" my={2} _hover={{cursor: 'pointer'}}>
        <UiLink as={Link} to={fullPath} _hover={{color: 'link.active'}}>
          {item.label}
        </UiLink>
      </UiBox>
    );
  }
  if (isMobile) {
    return (
      <UiBox key={`menu-${fullPath}`} fontWeight="bold" _hover={{cursor: 'pointer'}}>
        <Row alignItems="flex-end" my={5} pos={isProductsPath ? 'relative' : undefined}>
          <UiLink
            onClick={() => cb && cb()}
            as={Link}
            to={fullPath}
            fontSize="xl"
            color={isActive && 'orange.400'}
            _focus={{boxShadow: 'none'}}
            _hover={{color: isActive ? undefined : 'orange'}}
            borderBottom={isActive ? '1px' : 0}
            borderBottomColor="orange"
            zIndex={2}
          >
            {item.label}
          </UiLink>
          {isProductsPath && (
            <UiImage
              filePath="products-circle.png"
              pos="absolute"
              h={{base: '3em', lg: '4em'}}
              w={{base: '7em', lg: '5.7em'}}
              top={{base: '-8px', lg: '-15px'}}
              left={{base: '-10px', lg: '5px'}}
              zIndex={1}
            />
          )}
        </Row>
        {/*<UiDivider borderColor="orange.400" />*/}
      </UiBox>
    );
  }

  return (
    <UiBox key={`menu-${fullPath}`} fontWeight="bold" _hover={{cursor: 'pointer'}}>
      <Row px={5} alignItems="flex-end" pos={isProductsPath ? 'relative' : undefined}>
        <UiLink
          as={Link}
          _focus={{boxShadow: 'none'}}
          to={fullPath}
          _hover={{color: isActive ? undefined : 'orange'}}
          borderBottom={isActive ? '1px' : 0}
          borderBottomColor="orange"
          zIndex={2}
        >
          {item.label}
        </UiLink>
        {isProductsPath && (
          <UiImage
            filePath="products-circle.png"
            pos="absolute"
            h={{base: '9em', lg: '4em'}}
            w={{base: '9em', lg: '5.7em'}}
            top={{base: '-5px', lg: '-15px'}}
            left={{base: '-1px', lg: '11px'}}
            zIndex={1}
          />
        )}
      </Row>
    </UiBox>
  );
};

export const Navigation = memo((props: Props) => {
  const dispatch: AppDispatch = useDispatch();
  const {user: currentUser} = getCurrentUserFromStorage();
  const history = useHistory();
  const ref = useRef<HTMLDivElement>(null);
  const [isAtTop, setIsAtTop] = React.useState(true);
  const [isDesktop] = useMediaQuery(`(min-width: ${QUERY_MOBILE})`, {ssr: false});
  const colorText = useColorModeValue('black', 'orange.400');
  const {pathname} = useLocation();
  const [isColorMatchedLetters, setIsColorMatchedLetters] = useState<boolean>(false);

  useEffect(() => {
    setIsColorMatchedLetters(isSubstringArrayInString(PAGES_BANNER_SAME_TEXT_COLOR_NAVIGATION, pathname));
  }, [pathname]);

  useEffect(() => {
    dispatch(setMenuHeight({height: ref?.current?.clientHeight!}));
    window.onscroll = () => {
      setIsAtTop(window.scrollY < 10);
    };
  }, [dispatch]);

  return currentUser == null ? null : (
    <UiBox
      ref={ref}
      pos="fixed"
      right={0}
      left={0}
      top={0}
      bg={!isAtTop ? 'white' : 'transparent'}
      color={isColorMatchedLetters && isAtTop ? 'white' : 'black'}
      zIndex={999}
      sx={{transition: 'all .3s ease-in'}}
      borderBottom={!isAtTop ? '2px' : '0'}
      borderStyle="solid"
      borderColor="#f1eee4"
    >
      <UiContainer maxW="container.xl">
        <UiGrid templateColumns="repeat(16, 1fr)" py={3} gap={6} alignItems="center">
          <UiGridItem colSpan={{base: 12, lg: 3}}>
            <Row align="flex-end">
              {!isDesktop && (
                <UiIcon
                  as={GiHamburgerMenu}
                  boxSize={'2.3rem'}
                  onClick={() => dispatch(showMobileMenu({value: true}))}
                  _hover={{cursor: 'pointer'}}
                />
              )}
              <UiLink actionName="Logo Rayyone" as={Link} to="/">
                <UiImage filePath="rayyone.svg" width={50} height="100%" objectFit="contain" />
              </UiLink>
              <UiLink actionName="Rayyone" as={Link} to="/">
                <UiText
                  _hover={{cursor: 'pointer'}}
                  fontWeight="bold"
                  color={isColorMatchedLetters || !isAtTop ? 'orange.400' : colorText}
                  fontSize="lg"
                  w="100%"
                  display="inline-block"
                >
                  | Rayyone
                </UiText>
              </UiLink>
            </Row>
          </UiGridItem>
          {!isDesktop && <MobileNavigation />}
          {isDesktop && (
            <UiGridItem colStart={4} colEnd={-1}>
              <Row alignItems="center" justifyContent="flex-end">
                {publicRoutes.map(item => {
                  return renderMenuItems(item);
                })}
                <UiButton
                  variant="outline"
                  colorScheme="white"
                  onClick={() => {
                    history.push(PAGES.CONTACT_US);
                  }}
                >
                  Contact us!
                </UiButton>
              </Row>
            </UiGridItem>
          )}
        </UiGrid>
      </UiContainer>
    </UiBox>
  );
});
